<script setup lang="ts">
import { ref, reactive, computed, watch, onBeforeMount, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue'
import { isToday } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

import { useAdaMoltenBundle } from '../../../../composables/use-ada-molten-bundle'
import { useDeviceDetection } from '../../../../composables/use-device-detection'
import { useEpgAirTimeHelper } from '../../../../composables/use-epg-air-time-helper'
import { useEpgStructuredData } from '../../../../composables/use-epg-structured-data'
import { useEpgWidgetTracking } from '../../../../composables/use-epg-widget-tracking'
import EventBus from '../../../../event-bus/EventBus'
import { useConfigStore } from '../../../../stores/config'
import { usePageStore } from '../../../../stores/page'
import { useAdaStore } from '../../../../stores/ada'
import BroadcastList from '../EpgSearch/BroadcastList.vue'
import FilterContainer from '../EpgSearch/Filter/Container.vue'
import { hashString } from '../../../../utils/strings'
import { useAdaDynamicSlotMarker } from '../../../../composables/use-ada-dynamic-slot-marker'
import { isUserTabbing } from '../../../../composables/use-keep-tabbing-focus'
import { useClient } from '../../../../utils/client-only'
import { getAxios } from '../../../../api/axios'

import type { EpgWidgetParagraph } from '../../../../utils/paragraph-schema/paragraphs'
import type { FilterState } from '../EpgSearch/types'

declare global {
  interface Window {
    // debugging helpers used by `next-frontend-dev-toolkit.js`, see there for details
    bxEpgDebug?: Record<string, { initialFilters: InitialFilters, selectedFilters: FilterState }>,
    bxEpgInstances?: string[]
  }
}

type EpgWidget = EpgWidgetParagraph['epgWidget']
type EpgResultSet = NonNullable<EpgWidget['initialBroadcasts']>[number]

type Config = NonNullable<EpgWidget['config']>
type InitialFilters = NonNullable<EpgWidget['initialFilters']>
type Channels = NonNullable<EpgWidget['channels']>

type RestorableEpgPageData = {
  updatedAt: number,
  selectedFilters: FilterState,
  broadcasts: EpgResultSet[],
  searchMeta: { total: number | null, offset: number | null, limit: number | null }
}

const props = withDefaults(defineProps<{
  /**
   * Possible prop for vueComponent, maybe used to pass filter settings for integration in different contexts
   * i.e. [{"key":"filter","value":"blafasel-json-string-with-filter-setting"},{"key":"filterB","value":"or-separat-parameter-for-each-filter-option"}]
   */
  params?: Array<{ key: string, value: string }> | null,
  config?: Config,
  initialFilters?: InitialFilters,
  initialBroadcasts?: EpgResultSet[],
  channels?: Channels,
  isPreview?: boolean
}>(), {
  isPreview: false
})

const { isClient } = useClient()
const { deviceType } = useDeviceDetection()
const { timeZone } = useEpgAirTimeHelper()
const registerSlots = ref<string[]>([])
const { adaSlotReloadOnInteraction } = useAdaMoltenBundle({ registerSlots, deviceType })
const { injectMarkerAndAllocateAdSlotsInDynamicContent, injectMarkerToEpgSearch } = useAdaDynamicSlotMarker('')
const { trackEventInDataLayer } = useEpgWidgetTracking()

const configStore = useConfigStore()
const pageStore = usePageStore()
const adaStore = useAdaStore()

// Needed to reach the component's root element in `onBeforeMount`: the `rootEl` template ref is only
// populated on mount, but during SSR hydration the instance's `vnode.el` already points at the
// pre-rendered DOM node (the Composition-API equivalent of the old Options-API `this.$el`).
const instance = getCurrentInstance()
const rootEl = ref<HTMLElement | null>(null)
const epgIdentifier = ref<string | null>(null)
const progressSearch = ref(false)
const selectedFilters = ref<FilterState>({ categoryIds: [] })
const broadcasts = ref<EpgResultSet[]>(props.initialBroadcasts ?? [])
const broadcastsLoadedTotal = ref(0)
const broadcastsResultSetsTotal = ref(0)
const isFiltersInitialized = ref(false) // flag for initial search
const isRestoringState = ref(false) // true while applying persisted session-storage state after hydration
const searchMeta = reactive<{ total: number | null, offset: number | null, limit: number | null }>({
  total: null,
  offset: null,
  limit: null
})

const allowSessionStorage = ref(false)
const sessionStorageDuration = 5 * 60 * 1000 // 5 minutes
// Persisted state for THIS instance, read in `onBeforeMount` (to decide up front whether we'll restore and
// therefore skip the initial fetch) but only applied to the render in `onMounted`, i.e. after hydration.
let pendingRestore: RestorableEpgPageData | null = null
// Drives the FilterContainer's `restored-filters` prop; stays null until the restore is applied post-hydration.
const restoredFilters = ref<FilterState>()
const sessionStorageItemName = reactive({
  searchData: 'epgSearchData',
  scrollPosition: 'epgScrollPosition'
})

// registers the schema.org structured data (json-ld) for the current broadcasts, replaces the
// `epgStructuredDataBroadcastItemList` mixin's `head()` hook
useEpgStructuredData({
  initialFilters: computed(() => props.initialFilters ?? {}),
  config: computed(() => props.config ?? {}),
  broadcasts
})

const isChannelDetailPage = computed(() => {
  return pageStore.pageData?.pageMeta?.url?.includes('/tv/sender-')
})

const channelIds = computed(() => {
  // on a channel detail page (e.g. /tv/sender-ard) the single channel comes from the page meta node id;
  if (isChannelDetailPage.value) {
    const nodeId = pageStore.pageData?.pageMeta?.nodeId
    return nodeId ? [nodeId] : []
  }

  // otherwise the editor-configured channel(s)
  return props.initialFilters?.channelIds || []
})

const viewMode = computed(() => {
  // if the page is a channel detail page, we will set the view mode to "channel"
  return isChannelDetailPage.value ? 'channel' : 'overview'
})

const isMultiChannelSplitRequest = computed(() => {
  return !(
    channelIds.value.length ||
    selectedFilters.value.phrase?.length ||
    selectedFilters.value.categoryIds.length ||
    selectedFilters.value.genreIds?.length ||
    selectedFilters.value.isDailyTip ||
    selectedFilters.value.isLive ||
    selectedFilters.value.isNew
  )
})

const moreToLoad = computed(() => {
  return isMultiChannelSplitRequest.value ? (searchMeta.total ?? 0) > broadcastsResultSetsTotal.value : (searchMeta.total ?? 0) > broadcastsLoadedTotal.value
})

const reloadAdsOnInteraction = computed(() => {
  return configStore.rsConfig.adSlotUsage?.reloadAdsOnInteraction
})

const noResultErrorMessage = computed(() => {
  const title = '<strong>Keine passenden Sendungen gefunden.</strong><br><br>'

  // Let's differentiate between the two cases, case 1: no filters available, case 2: user can change filters
  const description = props.config?.hideFilters || props.config?.isThematic
    ? 'Aktuell liegen in dieser Auswahl keine Sendungen vor.<br><br>Schau gerne später noch einmal vorbei – das Programm wird laufend aktualisiert.'
    : 'Vielleicht läuft dein Wunschprogramm zu einer anderen Zeit oder auf einem anderen Sender?<br><br>Passe einfach die Filter an und entdecke mehr Inhalte!'

  return title + description
})

function handleEpgAdSlotInjection () {
  injectMarkerAndAllocateAdSlotsInDynamicContent(() => { injectMarkerToEpgSearch() })
}

function storeLastScrollY () {
  // store the last scroll position in session storage
  sessionStorage.setItem(sessionStorageItemName.scrollPosition, String(window.scrollY))
}

function setActualVh () {
  document.documentElement.style.setProperty('--epg-filter-vh', `${window.innerHeight}px`)
}

/*
 * Update the selected filters with new values.
 * Merges the new filters with the existing ones.
 */
function updateFilters (newFilters: FilterState) {
  selectedFilters.value = { ...selectedFilters.value, ...newFilters }

  if (isFiltersInitialized.value && !isRestoringState.value) {
    triggerAdReload()
    // Google Analytics tracking
    trackEventInDataLayer('epg_filter_result_page_view', {
      timeDate: selectedFilters.value.airTimeOptions,
      programType: selectedFilters.value.categoryIds.map(catId => Object.assign({}, ...(configStore.rsConfig.epg?.broadcastCategories || []))[parseInt(catId)]).join(', ') || 'none',
      channel: selectedFilters.value.packageName,
      tipOfTheDay: selectedFilters.value.isDailyTip ? 'on' : 'off'
    })
  }

  if (allowSessionStorage.value) {
    window.bxEpgDebug![epgIdentifier.value!] = {
      initialFilters: JSON.parse(JSON.stringify(props.initialFilters ?? {})),
      selectedFilters: JSON.parse(JSON.stringify(selectedFilters.value))
    }
  }
}

/**
 * Handle the event when filters are ready.
 * This method updates the selected filters and fetches broadcasts if there are none delivered yet.
 */
function onFiltersReady (newFilters: FilterState) {
  updateFilters(newFilters)

  // set the default values for offset and limit
  searchMeta.offset = selectedFilters.value.offset ?? null
  searchMeta.limit = selectedFilters.value.limit ?? null
  searchMeta.total = selectedFilters.value.total ?? null

  nextTick(() => {
    isFiltersInitialized.value = true
  })

  // If we'll restore persisted state after hydration, don't start an initial request now: it would race the
  // restore and, once it resolves, overwrite the restored broadcasts/searchMeta with results for the
  // server-initial filters (and persist that mismatch). The restore populates the broadcasts instead.
  if (pendingRestore?.selectedFilters) return

  if (!props.initialBroadcasts?.length) {
    // if there are no broadcasts delivered yet, start the first search
    fetchBroadcasts()
  } else {
    if (!import.meta.env.SSR && adaStore.isAsyncContentMode && broadcastsLoadedTotal.value > 0) {
      // Unlike standard and VOD searches, the EPG search already delivers composer initial broadcasts.
      // In this case, triggering via the watcher doesn't work and we have to inject the marker directly
      handleEpgAdSlotInjection()
    }
    triggerBroadcastsValidation()
  }
}

/*
 * Fetch broadcasts from the EPG search service.
 * If "loadMore" is true, it will load more broadcasts based on the current offset.
 * Otherwise, it will reset the offset and fetch fresh results.
 */
async function fetchBroadcasts (loadMore = false) {
  if (progressSearch.value || import.meta.env.SSR) {
    return
  }

  // while timing is everything in this async logic, we need a flag to prevent early ad reloads
  // we explicitly have to wait until content is completely loaded
  adaStore.setAsyncContentReadyForAdReload(false) // reset the async content ready for reload flag

  progressSearch.value = true
  const url = configStore.rsConfig.epgSearchServiceUrl // 'http://localhost:3200/v2/epg-search'
  const params: Record<string, unknown> = {}

  // If "loadMore" is true, increase offset; otherwise, reset to 0 for a new search
  searchMeta.offset = loadMore ? Number(searchMeta.offset) + Number(searchMeta.limit) : 0

  const { airTimeOptions, airTime, phrase, packageName, categoryIds, genreIds, isDailyTip, isLive, isNew, payTvOnly, freeTvOnly } = selectedFilters.value

  // we only need to set the airTime if actively selected by the user via the filter form (e.g. 20:15, 22:00, custom);
  // for 'now' we normally let the backend resolve the actual live current time itself - unless the user kept 'now'
  // selected while browsing a different day, in which case we have to pin down that day's "current time" explicitly
  const isNowButNotToday = airTimeOptions === 'now' && airTime && !isToday(toZonedTime(new Date(airTime), timeZone))
  if (airTimeOptions !== 'now' || isNowButNotToday) {
    params.airTime = airTime
  }

  // search phrase is now also supported in the default epg-search route
  if (phrase && phrase.length > 0) params.phrase = phrase

  // if there are channel id(s) (e.g. on a channel detail page or a channel-scoped widget), we send them;
  // otherwise we only set the package name if it is not "all"
  if (channelIds.value.length) {
    params.channelIds = channelIds.value.join(',')
  } else if (packageName !== 'all') {
    params.packageName = packageName
  }

  // append categoryIds if there are any selected
  if (categoryIds.length > 0) {
    params.categoryIds = categoryIds.join(',')
  }

  // append `genreIds` if there are any selected
  if (genreIds && genreIds.length > 0) {
    params.genreIds = genreIds.join(',')
  }

  // and if daily tips are selected, we will append it to the params also
  if (isDailyTip) {
    params.isDailyTip = isDailyTip
  }

  // more flags
  if (isLive) params.isLive = isLive
  if (isNew) params.isNew = isNew
  if (payTvOnly) params.payTvOnly = payTvOnly
  if (freeTvOnly) params.freeTvOnly = freeTvOnly

  params.offset = searchMeta.offset
  params.limit = searchMeta.limit

  try {
    const axios = await getAxios()
    const { data } = await axios.get(url!, { params })
    searchMeta.total = data.total
    broadcasts.value = loadMore ? [...broadcasts.value, ...data.results] : data.results || []
  } catch (error) {
    console.error('Error fetching broadcasts:', error)
  }

  saveEpgPageData()
  progressSearch.value = false

  if (loadMore) {
    triggerAdReload()
  }

  if (isUserTabbing()) {
    nextTick(() => {
      const brotkasten = rootEl.value?.querySelectorAll<HTMLElement>('.bx-epg-channel')
      if (!brotkasten) return
      !loadMore ? brotkasten[0]?.focus() : brotkasten[brotkasten.length - 10]?.focus()
    })
  }
}

function triggerAdReload () {
  if (reloadAdsOnInteraction.value?.enabled && allowSessionStorage.value) {
    // Trigger AGF tracking on ad reload
    EventBus.$emit('triggerAGF')

    // Schedule ad reload OUT of the input event to avoid blocking the input event
    setTimeout(() => {
      adaSlotReloadOnInteraction({ ...reloadAdsOnInteraction.value!.config, clickCount: 1 })
    }, 0)
  }
}

function initSessionStorage () {
  // define Identifier for this Instance to prevent problems with multiple instances on the same page.
  // Use the hydrated `vnode.el` (available already in `onBeforeMount`, unlike `rootEl`) so the restore
  // below can run before the FilterContainer initializes from `initialFilters`.
  const rootElement = (instance?.vnode.el ?? rootEl.value) as HTMLElement | null
  const epgOrderPosition = rootElement?.closest('[data-lop]')?.getAttribute('data-lop') || 'Unknown'
  epgIdentifier.value = `epgPos${epgOrderPosition}`
  // check if we have a valid identifier and we are not in preview mode of the GUI
  if (!pageStore.isPreview && epgIdentifier.value !== 'epgPosUnknown') {
    // allow session storage and create array in global scope to get known how many instances are on the page
    allowSessionStorage.value = true

    // make the session storage unique for each url, so that the user can navigate between different epg pages without losing the data
    const hrefHash = hashString(window.location.href)
    sessionStorageItemName.searchData += '_' + hrefHash
    sessionStorageItemName.scrollPosition += '_' + hrefHash

    window.bxEpgInstances = window.bxEpgInstances || []
    window.bxEpgInstances.push(epgIdentifier.value)
    window.bxEpgDebug = window.bxEpgDebug || {}
  }
}

/*
 * Save the current epgSearchData for this instance in session storage.
 * This is used to restore the data when the user comes back to the page.
 * The data is saved in the session storage with a unique identifier for this instance.
 * This includes selectedFilters, broadcasts, searchMeta values, the last URL as scope
 * and time of last page view.
 */
function saveEpgPageData () {
  // check if we have a valid identifier
  if (!allowSessionStorage.value) return
  const epgSearchData = JSON.parse(sessionStorage.getItem(sessionStorageItemName.searchData) || '{}')
  epgSearchData.scope = window.location.href
  epgSearchData.lastPageViewAt = new Date().getTime()
  epgSearchData.epgInstances = window.bxEpgInstances
  epgSearchData[epgIdentifier.value!] = {
    updatedAt: new Date().getTime(),
    selectedFilters: selectedFilters.value,
    broadcasts: broadcasts.value,
    searchMeta: { ...searchMeta }
  }
  sessionStorage.setItem(sessionStorageItemName.searchData, JSON.stringify(epgSearchData))
}

/*
 * Read and validate the persisted state for this instance from session storage.
 * Returns the stored payload (which may legitimately contain an empty broadcasts array) or null when there
 * is nothing valid to restore - e.g. a foreign scope or an expired last page view, in which case the stale
 * data is pruned. Runs in `onBeforeMount` so we can decide up front whether to skip the initial fetch; it
 * must not touch reactive render state (that would break hydration).
 */
function readRestorableEpgPageData (): RestorableEpgPageData | null {
  // check if we have a valid identifier
  if (!allowSessionStorage.value) return null

  // load the epg page data from session storage
  const epgSearchData = JSON.parse(sessionStorage.getItem(sessionStorageItemName.searchData) || '{}')

  const isDurationValid = (epgSearchData.lastPageViewAt + sessionStorageDuration) > new Date().getTime()
  // check if we are in the same scope as the last saved data and if the duration is valid
  if (epgSearchData.scope !== window.location.href || !isDurationValid) {
    // stale (expired) or foreign scope -> drop the old data
    sessionStorage.removeItem(sessionStorageItemName.searchData)
    sessionStorage.removeItem(sessionStorageItemName.scrollPosition)
    return null
  }

  // keep the data alive for the next detail-page round-trip
  epgSearchData.lastPageViewAt = new Date().getTime()
  sessionStorage.setItem(sessionStorageItemName.searchData, JSON.stringify(epgSearchData))

  return epgSearchData[epgIdentifier.value!] ?? null
}

/*
 * Apply the persisted search state (filters, broadcasts, searchMeta, scroll) read by
 * `readRestorableEpgPageData`. Called from `onMounted`, i.e. after hydration, so the first client render
 * still matches the server. The restored filters reach the FilterContainer via the reactive
 * `restored-filters` prop; the single refetch their change would trigger is suppressed via `isRestoringState`,
 * because the matching broadcasts (including a legitimately empty result) are restored here.
 */
function applyRestoredState () {
  if (!pendingRestore?.selectedFilters) return

  isRestoringState.value = true

  if (pendingRestore.searchMeta) {
    Object.assign(searchMeta, pendingRestore.searchMeta)
  }
  // Assign even an empty result set: it's the authoritative outcome of the restored filters. Keeping the
  // server initial broadcasts would show hits that don't match the restored filters.
  broadcasts.value = pendingRestore.broadcasts ?? []

  // hand the restored filters to the FilterContainer (applies its selection post-hydration)
  restoredFilters.value = pendingRestore.selectedFilters

  // restore scroll only after the restored broadcasts have rendered so the target height already exists
  const lastScrollPosition = sessionStorage.getItem(sessionStorageItemName.scrollPosition)
  if (lastScrollPosition) {
    nextTick(() => window.scrollTo(0, parseInt(lastScrollPosition, 10)))
  }
}

/**
 * Trigger the validation of broadcasts.
 * This method is called for initial and session storage data.
 */
function triggerBroadcastsValidation () {
  // If there is no multi-channel split request or the view mode is "channel", we don't validate the broadcasts, cause they are not specific for a specific time
  if (!isMultiChannelSplitRequest.value || viewMode.value === 'channel') return

  // Trigger the validation of broadcasts
  const toleranceMs = 2.5 * 60 * 1000 // 2.5 minutes
  const validationResult = validateBroadcasts(broadcasts.value, selectedFilters.value.airTime!, toleranceMs)

  if (validationResult.percentValid < 70) {
    // If there are less than 70 percent valid items, we fetch actual broadcasts from the server
    fetchBroadcasts()
  }
}

/**
 * Prüft, ob der erste Broadcast je Channel zum gegebenen Zeitpunkt passt
 * @param epgData - Liste der Channels inkl. Broadcasts
 * @param airTime - Der Zeitpunkt, der geprüft werden soll (z.B. neue Date() oder ISO-String)
 * @param toleranceMs - Erlaubte Abweichung in Millisekunden (Standard: 0)
 */
function validateBroadcasts (epgData: EpgResultSet[], airTime: string, toleranceMs = 0) {
  const timeToCheck = new Date(airTime).getTime()

  let valid = 0
  let invalid = 0

  for (const item of epgData) {
    const broadcast = item.broadcasts?.[0]
    if (!broadcast) {
      invalid++
      continue
    }

    const start = new Date(broadcast.airTime!).getTime()
    const end = new Date(broadcast.airTimeEnd!).getTime()

    const inRange =
      timeToCheck >= start - toleranceMs &&
      timeToCheck <= end + toleranceMs

    if (inRange) {
      valid++
    } else {
      invalid++
    }
  }

  const total = valid + invalid
  const percentValid = total > 0 ? (valid / total) * 100 : 0

  return {
    total,
    valid,
    invalid,
    percentValid: Math.round(percentValid * 100) / 100
  }
}

// watch for changes in the selected filters and fetch broadcasts accordingly
watch(selectedFilters, () => {
  if (!isFiltersInitialized.value) return

  // The one filter change caused by restoring persisted filters must not refetch - the matching broadcasts
  // were already restored from session storage. Consume the flag once, then resume normal fetching. Only a
  // non-empty restored result is re-validated: an empty one is a legitimate outcome of the restored filters
  // (category/phrase/channel), which the airTime-only validation cannot judge anyway.
  if (isRestoringState.value) {
    isRestoringState.value = false
    if (broadcasts.value.length) triggerBroadcastsValidation()
    return
  }

  fetchBroadcasts()
}, { deep: true })

// watch for changes in the broadcasts array and update the loaded total
watch(broadcasts, (newVal) => {
  broadcastsLoadedTotal.value = newVal.reduce((sum, resultSet) => sum + resultSet.broadcasts.length, 0)
  broadcastsResultSetsTotal.value = newVal.length

  // We've added the check on epgIdentifier to ensure that the code is only executed when the component has been completely initialized
  // Otherwise, this would result in duplicate slot markers if multiple epgWidgets are on a page.
  if (!import.meta.env.SSR && adaStore.isAsyncContentMode && broadcastsLoadedTotal.value > 0 && epgIdentifier.value) {
    // if we are in async content mode and there are broadcasts loaded, we will inject new marker to the async content container
    // the callback function contains the specific logic for the content of this component
    handleEpgAdSlotInjection()
  }
}, { immediate: true, deep: true })

onBeforeMount(() => {
  initSessionStorage()
  // Decide up front - before FilterContainer emits `filters-ready` and onFiltersReady may start a request -
  // whether we'll restore, so the initial fetch can be skipped and won't race/overwrite the restored state.
  pendingRestore = readRestorableEpgPageData()
  // cause this componente is used on different pages, we need to check if we are on a TV page
  // for example: /tv/sender-ard or /tv/programm and NOT on homepage or other pages where we just have a small widget
  const allowAsyncContent = window.location.pathname.includes('/tv/')
  // inform molten bundle logic, that we want to place ad slots in async content mode
  adaStore.setAsyncContentMode(allowAsyncContent)
})

onMounted(() => {
  // Apply persisted state only after hydration so the first client render still matches the
  // server-rendered initial filters (otherwise Vue reports hydration mismatches).
  applyRestoredState()

  // before the user leaves the page, we will save the scroll position in session storage
  // to restore it when the user comes back
  window.addEventListener('beforeunload', storeLastScrollY)
  window.addEventListener('resize', setActualVh)
  window.addEventListener('orientationchange', setActualVh)
  setActualVh()
})

onBeforeUnmount(() => {
  // remove the event listeners to avoid memory leaks
  window.removeEventListener('beforeunload', storeLastScrollY)
  window.removeEventListener('resize', setActualVh)
  window.removeEventListener('orientationchange', setActualVh)
})
</script>

<template>
  <div ref="rootEl"
       :key="epgIdentifier ?? undefined"
       class="bx-content bx-search bx-typo--search bx-typo--epg bx-typo--vod-epg bx-epg-search"
       :class="{
         [`bx-epg-search--${viewMode}`]: true,
         'bx-epg-search--hide-filters': config?.hideFilters
       }">
    <FilterContainer :initial-filters="initialFilters"
                     :restored-filters="restoredFilters"
                     :view-mode="viewMode"
                     :config="config"
                     :channels="channels"
                     :is-preview="isPreview"
                     @update-filters="updateFilters($event)"
                     @filters-ready="onFiltersReady($event)" />
    <section class="bx-epg-search__broadcasts"
             :class="{ 'bx-js-loading': progressSearch }">
      <div v-if="progressSearch"
           class="bx-form__loader" />
      <BroadcastList v-if="broadcasts.length"
                     :epg-data="broadcasts"
                     :view-mode="viewMode"
                     :card-layout="!!config?.isThematic && !isChannelDetailPage"
                     :use-sports-layout="!!config?.useSportsLayout" />
      <div v-else-if="!progressSearch"
           class="bx-search__no-results">
        <span v-html="noResultErrorMessage" />
      </div>

      <div v-if="isClient && (moreToLoad || config?.loadMoreUrl)"
           class="bx-form__item bx-form__item--centered bx-typo--form">
        <a v-if="config?.loadMoreUrl"
           :href="config?.loadMoreUrl"
           class="bx-form__button"
           @click="trackEventInDataLayer('epg_load_more_click', { click_url: config?.loadMoreUrl })">
          Mehr Laden
        </a>
        <button v-else-if="moreToLoad"
                class="bx-form__button"
                type="button"
                @click.prevent="trackEventInDataLayer('epg_load_more_pageview');
                                fetchBroadcasts(true)">
          Mehr Laden
        </button>
      </div>
    </section>
  </div>
</template>
